import React from "react";
import { Link } from "react-router-dom";
import { Button, Spinner } from "flowbite-react";
import { HiExternalLink } from "react-icons/hi";
import { useTranslation, useAuthentication } from "@repo/core";
import { useStripeCheckout } from "../../hooks/useStripeCheckout";

/** Entry point to the Stripe customer portal (invoices, payment method,
 * cancellation). Reuses the same session-creating hook the navbar menu item
 * uses, so both paths stay in sync.
 *
 * The portal is keyed by `stripeCustomerId`; a user who has never checked out
 * doesn't have one yet, so they're pointed at /subscriptions instead of a
 * button that could only fail. */
const BillingPortalCard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthentication();
  const { createCustomerPortalSession, customerPortalLoading } = useStripeCheckout();

  const hasBillingAccount = Boolean(user?.stripeCustomerId);

  return (
    <section className="rounded-lg bg-white p-8 shadow dark:bg-gray-800">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
        {t("profile.billing.title")}
      </h3>
      <p className="mt-1 mb-6 text-sm text-gray-600 dark:text-gray-400">
        {hasBillingAccount
          ? t("profile.billing.description")
          : t("profile.billing.noAccountDescription")}
      </p>

      {hasBillingAccount ? (
        <Button
          color="light"
          className="w-full"
          onClick={createCustomerPortalSession}
          disabled={customerPortalLoading}
        >
          {customerPortalLoading ? (
            <Spinner size="sm" className="mr-2" />
          ) : (
            <HiExternalLink size={18} className="mr-2" />
          )}
          {t("profile.billing.openPortal")}
        </Button>
      ) : (
        <Button as={Link} to="/subscriptions" color="light" className="w-full">
          {t("profile.billing.viewPlans")}
        </Button>
      )}
    </section>
  );
};

export default BillingPortalCard;
